import { WidgetViewer } from '@juvare/widget-ui/WidgetViewer';
import React from 'react';
import { FormConfigContext } from '../FormConfigContext';
import { defaultFormWidgets } from '../WidgetForm';
import { applyRules } from '../WidgetForm/Utils';
/**
 * Low level form component. Differs from Form component that it will rerender when props change.
 * This component should be used if you need to build some higher level component that regular Form does not cover.
 *
 * @template RootConfig - The type of the root widget configuration.
 * @param {FormViewerProps<RootConfig>} props - The props for configuring the FormViewer component.
 * @param {RootConfig} props.config - The root widget configuration for rendering the form.
 * @param {any} [props.data] - The form data to bind to the form fields.
 * @param {Widget<any, any>[]} [props.widgets] - Optional list of widget definitions to use.
 * @param {'all' | 'changed' | 'none'} [props.showErrors] - Controls when validation errors should be shown.
 * @param {ValueProvider[]} [props.valueProviders] - Optional providers for dynamic values within the form.
 * @param {SubFormProvider[]} [props.subFormProviders] - Optional providers for nested forms/subforms.
 * @param {Rule<any>[]} [props.rules] - Optional array of form behavior or validation rules.
 * @param {GlobalWidgetConfig} [props.globalWidgetConfig] - Optional configuration applied to all widgets.
 * @param {Record<string, any>} [props.formParams] - Optional parameters passed to the form instance.
 * @param {(data: WidgetData) => void} [props.onChange] - Callback when the form data changes.
 * @param {(data: WidgetData, immediately: boolean) => void} [props.onAutoSave] - Callback for auto-saving form data.
 * @param {(data: WidgetData) => void} [props.onDataChange] - Callback for tracking form data updates.
 * @param {boolean} [props.readOnly] - Optional flag to toggle read-only mode via form parameters.
 * @returns {React.ReactElement} The rendered FormViewer component.
 */
export function FormViewer(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const context = React.useContext(FormConfigContext);
    const valueProviders = (_b = (_a = props.valueProviders) !== null && _a !== void 0 ? _a : context.valueProviders) !== null && _b !== void 0 ? _b : emptyValueProviders;
    const subFormProviders = (_d = (_c = props.subFormProviders) !== null && _c !== void 0 ? _c : context.subFormProviders) !== null && _d !== void 0 ? _d : emptySubFormProviders;
    const rules = (_f = (_e = props.rules) !== null && _e !== void 0 ? _e : context.rules) !== null && _f !== void 0 ? _f : emptyRules;
    const widgets = (_h = (_g = props.widgets) !== null && _g !== void 0 ? _g : context.widgets) !== null && _h !== void 0 ? _h : defaultFormWidgets;
    const globalWidgetConfig = (_k = (_j = props.globalWidgetConfig) !== null && _j !== void 0 ? _j : context.globalWidgetConfig) !== null && _k !== void 0 ? _k : emptyGlobalWidgetConfig;
    const formParams = Object.assign(Object.assign({}, ((_m = (_l = props.formParams) !== null && _l !== void 0 ? _l : context.formParams) !== null && _m !== void 0 ? _m : {})), { readOnly: props.readOnly });
    const rootParams = React.useMemo(() => {
        var _a;
        return ({
            showErrors: (_a = props.showErrors) !== null && _a !== void 0 ? _a : 'all',
            valueProviders,
            subFormProviders,
            rules,
            globalWidgetConfig,
            formParams,
            onChange: props.onChange,
            onAutoSave: props.onAutoSave,
        });
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.showErrors, props.formParams, valueProviders, subFormProviders, rules, context.formParams]);
    React.useEffect(() => {
        rootParams.onChange = props.onChange;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onChange]);
    React.useEffect(() => {
        rootParams.onAutoSave = props.onAutoSave;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.onAutoSave]);
    const config = React.useMemo(() => {
        applyRules(props.config, widgets, props.data, props.formParams, rules);
        return props.config;
    }, [props.config, props.data, props.formParams, rules, widgets]);
    return React.createElement(WidgetViewer, Object.assign({}, props, { config: config, rootParams: rootParams, widgets: widgets, data: (_o = props.data) !== null && _o !== void 0 ? _o : emptyData }));
}
const emptyData = {};
const emptyValueProviders = [];
const emptySubFormProviders = [];
const emptyRules = [];
const emptyGlobalWidgetConfig = {};
